
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";

// Sample data - In a real implementation, this would come from Supabase
const sampleCustomerData = [
  { name: 'Free', value: 400, color: '#0088FE' },
  { name: 'Pro', value: 300, color: '#00C49F' },
  { name: 'Premium', value: 150, color: '#FFBB28' },
];

const sampleStudioData = [
  { name: 'Free', value: 100, color: '#0088FE' },
  { name: 'Pro', value: 200, color: '#00C49F' },
  { name: 'Business', value: 80, color: '#FF8042' },
  { name: 'Premium', value: 40, color: '#FFBB28' },
];

// Sample monthly user data - In a real implementation, this would come from Supabase
const sampleMonthlyData = [
  { name: 'Jan', users: 400, studios: 240 },
  { name: 'Feb', users: 500, studios: 280 },
  { name: 'Mar', users: 600, studios: 300 },
  { name: 'Apr', users: 780, studios: 340 },
  { name: 'May', users: 890, studios: 380 },
  { name: 'Jun', users: 1000, studios: 400 },
  { name: 'Jul', users: 1200, studios: 440 },
];

type TimeRange = '7days' | '30days' | '90days' | '1year' | 'all';

export function AdminAnalyticsCharts() {
  const [timeRange, setTimeRange] = useState<TimeRange>('30days');
  
  // In a real implementation, this would filter data based on timeRange
  const filteredData = sampleMonthlyData;
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold">Analytics Dashboard</h2>
        <div className="flex items-center gap-4">
          <Select
            value={timeRange}
            onValueChange={(value) => setTimeRange(value as TimeRange)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Zeitraum wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7days">Letzte 7 Tage</SelectItem>
              <SelectItem value="30days">Letzter Monat</SelectItem>
              <SelectItem value="90days">Letzte 3 Monate</SelectItem>
              <SelectItem value="1year">Letztes Jahr</SelectItem>
              <SelectItem value="all">Alle Daten</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">Daten aktualisieren</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Customer Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Nutzer nach Abonnements</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sampleCustomerData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sampleCustomerData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} Nutzer`, 'Anzahl']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Studio Distribution Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Studios nach Abonnements</CardTitle>
          </CardHeader>
          <CardContent className="h-[300px] flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={sampleStudioData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {sampleStudioData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value} Studios`, 'Anzahl']} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Monthly Active Users & Studios Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Monatlich aktive Nutzer & Studios</CardTitle>
        </CardHeader>
        <CardContent className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="users" name="Nutzer" fill="#2C6E49" />
              <Bar dataKey="studios" name="Studios" fill="#6B7280" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminAnalyticsCharts;
